import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { type BgtUser, createBgtUser, updateBgtUser } from '@/lib/api/credentials';
import {
  type BgtUserInputErrors,
  normalizeBirthInput,
  validateBgtUserInput,
} from '@/lib/bgt-user-input';
import { PROGRAM_LABEL } from '@/lib/program-labels';

export interface BgtUserFormInitial {
  id: string;
  patientCode: string | null;
  birthDate: string | null;
  name: string | null;
}

interface Props {
  open: boolean;
  /** 지정하면 수정 모드 */
  initial?: BgtUserFormInitial | null;
  onClose: () => void;
  onSaved: (user: BgtUser) => void;
}

export function BgtUserFormDialog({ open, initial, onClose, onSaved }: Props) {
  const editing = initial != null;

  const [patientCode, setPatientCode] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthConfirm, setBirthConfirm] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<BgtUserInputErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPatientCode(initial?.patientCode ?? '');
    setBirthDate(initial?.birthDate ?? '');
    setBirthConfirm(initial?.birthDate ?? '');
    setName(initial?.name ?? '');
    setErrors({});
    setSubmitError(null);
  }, [open, initial]);

  // 수정 모드에서 생년월일을 건드리지 않았다면 확인 입력을 요구하지 않는다.
  const birthChanged = !editing || birthDate !== (initial?.birthDate ?? '');

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        patientCode: patientCode.trim(),
        birthDate,
        name: name.trim() || undefined,
      };
      return editing ? updateBgtUser(initial.id, payload) : createBgtUser(payload);
    },
    onSuccess: (user) => {
      onSaved(user);
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof AxiosError) {
        const message = (err.response?.data as { message?: string | string[] } | undefined)
          ?.message;
        setSubmitError(
          (Array.isArray(message) ? message[0] : message) ?? '저장 중 오류가 발생했습니다',
        );
      } else {
        setSubmitError('저장 중 오류가 발생했습니다');
      }
    },
  });

  const submit = () => {
    setSubmitError(null);
    const found = validateBgtUserInput({
      patientCode,
      birthDate,
      birthConfirm,
      requireConfirm: birthChanged,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {editing
          ? `${PROGRAM_LABEL.bgt.full} 회원 정보 수정`
          : `${PROGRAM_LABEL.bgt.full} 회원 등록`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2" sx={{ mb: 2 }}>
          환자번호와 생년월일이 곧 앱 로그인 정보입니다. 한 글자라도 다르면 대상자가 앱에 로그인할
          수 없습니다.
        </DialogContentText>

        <Stack spacing={2}>
          <TextField
            label="환자번호"
            value={patientCode}
            onChange={(e) => setPatientCode(e.target.value)}
            error={!!errors.patientCode}
            helperText={errors.patientCode ?? '앱 로그인 ID로 쓰입니다'}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="생년월일"
            value={birthDate}
            onChange={(e) => setBirthDate(normalizeBirthInput(e.target.value))}
            error={!!errors.birthDate}
            helperText={errors.birthDate ?? '숫자 8자리 (예: 19450312)'}
            size="small"
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="생년월일 확인"
            value={birthConfirm}
            onChange={(e) => setBirthConfirm(normalizeBirthInput(e.target.value))}
            error={!!errors.birthConfirm}
            helperText={errors.birthConfirm ?? '오타 방지를 위해 한 번 더 입력합니다'}
            size="small"
            fullWidth
            disabled={!birthChanged}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="이름 (선택)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="BGT 앱에는 비어 있는 경우가 많습니다"
            size="small"
            fullWidth
          />

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? '저장 중…' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
